/**
 * PropertyInsights — the cross-source findings lens. Auriel's wow.
 *
 * Reads OPEN findings for the property (single-source from audits +
 * cross-source from the insights extractors), ranked by severity then
 * detected_at. Each card has a Markdown-rendered detail + an
 * "Evidence" drawer that links each row's source_data row in the
 * Inspector.
 *
 * The Refresh button kicks off a fresh cross-source extractor pass.
 */
import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Card } from "@tremor/react";
import {
  Sparkles, RefreshCw, Loader2, AlertTriangle, AlertCircle,
  Info, ChevronDown, ChevronRight, Database,
} from "lucide-react";
import {
  useProperty, useInsightsFindings, useExtractInsights,
  type InsightFinding,
} from "@/lib/queries";
import { cn } from "@/lib/utils";


export default function PropertyInsights() {
  const { propertyId: rawId } = useParams<{ propertyId: string }>();
  const propertyId = Number(rawId);
  const property = useProperty(propertyId);
  const findings = useInsightsFindings(propertyId);
  const extract = useExtractInsights(propertyId);

  const onExtract = async () => {
    try { await extract.mutateAsync(); } catch { /* surfaced inline */ }
  };

  if (property.isLoading) return <div className="text-sm text-muted-foreground">Loading…</div>;
  if (!property.data) return <div className="text-sm text-red-600">Property not found.</div>;
  const p = property.data;

  const items = findings.data?.items ?? [];
  const counts = items.reduce((acc, f) => {
    acc[f.severity] = (acc[f.severity] ?? 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            <h1 className="text-2xl font-semibold tracking-tight">Insights</h1>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            Cross-source findings for <span className="font-mono">{p.domain}</span> —
            things Auriel notices that no single SEO tool can produce because
            they require comparing multiple data sources at once.
          </p>
        </div>
        <button type="button" onClick={onExtract} disabled={extract.isPending}
          className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50">
          {extract.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          Re-run extractors
        </button>
      </div>

      {extract.error && (
        <div className="rounded-md border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-700 dark:text-red-400">
          {extract.error instanceof Error ? extract.error.message : "Extract failed"}
        </div>
      )}
      {extract.isSuccess && extract.data && (
        <div className="rounded-md border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-xs">
          <div className="font-medium text-emerald-700 dark:text-emerald-400">
            Extracted {extract.data.found} insights · {Object.entries(extract.data.by_severity).map(([s,n]) => `${n} ${s}`).join(" · ")}
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
        <SevPill label="Critical" count={counts.critical ?? 0} severity="critical" />
        <SevPill label="High"     count={counts.high ?? 0}     severity="high" />
        <SevPill label="Medium"   count={counts.medium ?? 0}   severity="medium" />
        <SevPill label="Low"      count={counts.low ?? 0}      severity="low" />
        <SevPill label="Info"     count={counts.info ?? 0}     severity="info" />
      </div>

      {findings.isLoading && (
        <Card><div className="text-sm text-muted-foreground">Loading findings…</div></Card>
      )}

      {!findings.isLoading && items.length === 0 && (
        <Card>
          <div className="text-center text-sm text-muted-foreground">
            <Sparkles className="mx-auto mb-2 h-6 w-6 opacity-50" />
            <p>No open findings yet.</p>
            <p className="mt-1">
              Click <strong>Re-run extractors</strong> to scan the substrate.
              The cross-source extractors look at GSC + GA4 + Ahrefs + DFS + server
              logs and detect patterns no single tool can.
            </p>
          </div>
        </Card>
      )}

      <div className="space-y-3">
        {items.map((f) => <FindingCard key={f.id} finding={f} />)}
      </div>
    </div>
  );
}


function SevPill({ label, count, severity }: {
  label: string; count: number;
  severity: "critical" | "high" | "medium" | "low" | "info";
}) {
  const cls = {
    critical: "border-red-500/40 bg-red-500/10 text-red-700 dark:text-red-400",
    high:     "border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-400",
    medium:   "border-blue-500/40 bg-blue-500/10 text-blue-700 dark:text-blue-400",
    low:      "border-slate-500/40 bg-slate-500/10 text-slate-700 dark:text-slate-400",
    info:     "border-border bg-muted text-muted-foreground",
  }[severity];
  return (
    <div className={cn("rounded-md border px-3 py-2", cls)}>
      <div className="text-[11px] uppercase tracking-wider">{label}</div>
      <div className="mt-0.5 text-2xl font-semibold">{count}</div>
    </div>
  );
}


function FindingCard({ finding: f }: { finding: InsightFinding }) {
  const [expanded, setExpanded] = useState(false);
  const [showEvidence, setShowEvidence] = useState(false);

  const sevIcon = {
    critical: <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-400" />,
    high:     <AlertTriangle className="h-4 w-4 text-amber-700 dark:text-amber-400" />,
    medium:   <AlertTriangle className="h-4 w-4 text-blue-700 dark:text-blue-400" />,
    low:      <Info className="h-4 w-4 text-slate-600 dark:text-slate-400" />,
    info:     <Info className="h-4 w-4 text-muted-foreground" />,
  }[f.severity];

  const sevBorder = {
    critical: "border-l-red-500",
    high:     "border-l-amber-500",
    medium:   "border-l-blue-500",
    low:      "border-l-slate-500",
    info:     "border-l-muted",
  }[f.severity];

  const evidence = (f.metadata?.evidence as Array<{ source_data_id: number; claim: string }> | undefined) ?? [];
  const sourceLabel = (f.metadata?.source as string | undefined) ?? "";

  return (
    <Card className={cn("border-l-4", sevBorder, "p-0 overflow-hidden")}>
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        className="flex w-full items-start justify-between gap-3 px-4 py-3 text-left hover:bg-accent/30"
      >
        <div className="flex min-w-0 items-start gap-2">
          {sevIcon}
          <div className="min-w-0 flex-1">
            <div className="flex items-baseline gap-2">
              <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
                {f.severity}
              </span>
              <h3 className="font-medium">{f.title}</h3>
            </div>
            <div className="mt-0.5 flex items-center gap-2 text-[11px] text-muted-foreground">
              <span className="font-mono">{f.kind}</span>
              {sourceLabel && <span>· source: <span className="font-mono">{sourceLabel}</span></span>}
            </div>
          </div>
        </div>
        {expanded ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
      </button>

      {expanded && (
        <div className="space-y-3 border-t border-border bg-muted/20 px-4 py-3">
          <div className="prose prose-sm max-w-none text-sm dark:prose-invert">
            <Markdown text={f.detail} />
          </div>

          {evidence.length > 0 && (
            <div className="rounded-md border border-border bg-background p-2">
              <button
                type="button"
                onClick={() => setShowEvidence((s) => !s)}
                className="flex items-center gap-1.5 text-[11px] font-medium text-muted-foreground hover:text-foreground"
              >
                <Database className="h-3 w-3" />
                Evidence ({evidence.length} source row{evidence.length === 1 ? "" : "s"})
                {showEvidence ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
              </button>
              {showEvidence && (
                <ul className="mt-2 space-y-1">
                  {evidence.map((e, i) => (
                    <li key={i} className="text-[11px]">
                      <Link
                        to={`/inspector?row_id=${e.source_data_id}`}
                        className="font-mono text-primary hover:underline"
                      >
                        source_data #{e.source_data_id}
                      </Link>
                      {" — "}
                      <span className="text-muted-foreground">{e.claim}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      )}
    </Card>
  );
}


/**
 * Minimal markdown renderer — handles bullet lists, **bold**, `code`,
 * paragraphs, code fences. Sufficient for finding details.
 */
function Markdown({ text }: { text: string }) {
  const lines = text.split("\n");
  const blocks: Array<{ type: "p" | "ul" | "code"; content: string[] }> = [];
  let current: { type: "p" | "ul" | "code"; content: string[] } | null = null;
  let inCode = false;

  for (const line of lines) {
    if (line.startsWith("```")) {
      inCode = !inCode;
      if (inCode) {
        if (current) blocks.push(current);
        current = { type: "code", content: [] };
      } else {
        if (current) blocks.push(current);
        current = null;
      }
      continue;
    }
    if (inCode) {
      current?.content.push(line);
      continue;
    }
    if (line.startsWith("- ") || line.startsWith("* ")) {
      if (current?.type !== "ul") {
        if (current) blocks.push(current);
        current = { type: "ul", content: [] };
      }
      current.content.push(line.slice(2));
    } else if (line.trim() === "") {
      if (current) { blocks.push(current); current = null; }
    } else {
      if (current?.type !== "p") {
        if (current) blocks.push(current);
        current = { type: "p", content: [] };
      }
      current.content.push(line);
    }
  }
  if (current) blocks.push(current);

  return (
    <>
      {blocks.map((b, i) => {
        if (b.type === "code") {
          return (
            <pre key={i} className="overflow-x-auto rounded bg-muted p-2 font-mono text-[11px]">{b.content.join("\n")}</pre>
          );
        }
        if (b.type === "ul") {
          return (
            <ul key={i} className="list-disc pl-5">
              {b.content.map((it, j) => <li key={j}><Inline text={it} /></li>)}
            </ul>
          );
        }
        return (
          <p key={i} className="whitespace-pre-wrap"><Inline text={b.content.join(" ")} /></p>
        );
      })}
    </>
  );
}


function Inline({ text }: { text: string }) {
  // **bold** and `code`
  const parts: Array<{ type: "text" | "bold" | "code"; t: string }> = [];
  const re = /(\*\*[^*]+\*\*|`[^`]+`)/g;
  let last = 0;
  let m;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push({ type: "text", t: text.slice(last, m.index) });
    const tok = m[0];
    if (tok.startsWith("**")) parts.push({ type: "bold", t: tok.slice(2, -2) });
    else parts.push({ type: "code", t: tok.slice(1, -1) });
    last = m.index + tok.length;
  }
  if (last < text.length) parts.push({ type: "text", t: text.slice(last) });

  return (
    <>
      {parts.map((p, i) => {
        if (p.type === "bold") return <strong key={i}>{p.t}</strong>;
        if (p.type === "code") return <code key={i} className="rounded bg-muted px-1 font-mono text-[11px]">{p.t}</code>;
        return <span key={i}>{p.t}</span>;
      })}
    </>
  );
}
